











import { PropType, computed, defineComponent, Ref, ref, watch } from '@vue/composition-api';
import { invertObj, isNil } from 'ramda';
import apexchart from 'vue-apexcharts';
import { useAxios } from '@/app/composable';
import { MetricsAPI } from '../api';
import { Asset } from '../types';
import { AssetType, AssetTypeId } from '../constants';
import { XCircleIcon, LightBulbIcon } from '@vue-hero-icons/solid';
import { ApexOptions } from 'apexcharts';

import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import { AssetMetricsQuery } from '../types/asset-metrics-query.type';
import { AxiosResponse } from 'axios';
import { SelfBuildingSquareSpinner } from 'epic-spinners';
import { Component } from 'vue/types/options';
import GraphError from './GraphError.vue';
import { useGraphUtils } from '../composable/graph-utils';

dayjs.extend(utc);

export default defineComponent({
    name: 'ReliabilityGraph',
    props: {
        asset: { type: Object as PropType<Asset>, required: true },
        query: { type: Object as PropType<AssetMetricsQuery>, required: true },
        height: { type: String, default: '300px' },
    },
    components: { apexchart, SelfBuildingSquareSpinner, GraphError },
    setup(props) {
        const { exec, loading } = useAxios(true);
        const { formatEpochBy } = useGraphUtils();
        const error: Ref<{ message: string; tooltip: string; icon: Component; classes: string } | undefined> = ref();

        const assetType: AssetType = invertObj(AssetTypeId)[props.asset.assetTypeId] as AssetType;

        const series: Ref<
            { name: string; data: { x: string; y: number; noMeasurement: boolean }[]; color?: string }[]
        > = ref([]);

        const options: Ref<ApexOptions> = computed(() => {
            return {
                chart: {
                    type: 'area',
                    zoom: { enabled: false },
                    animations: { enabled: false },
                },
                dataLabels: {
                    enabled: false,
                },
                yaxis: {
                    title: {
                        text: 'Reliability',
                    },
                    labels: {
                        formatter: (val: number, settings: { dataPointIndex: number }) => {
                            if (series.value.length > 0)
                                return !isNil(series.value[0].data[settings.dataPointIndex]) &&
                                    series.value[0].data[settings.dataPointIndex].noMeasurement
                                    ? `No measurement`
                                    : `${formatDecimals(val)}%`;
                            return ``;
                        },
                    },
                    min: 0,
                    max: 100,
                    tickAmount: 5,
                },
                xaxis: {
                    tickAmount: 'dataPoints',
                    type: 'category',
                    labels: {
                        rotate: -45,
                        formatter: (val: string): string | string[] => {
                            return formatEpochBy(val, props.query.granularity, true);
                        },
                    },
                    title: { text: 'Time' },
                },
            };
        });

        const refetch = () => {
            error.value = undefined;
            exec(MetricsAPI.getReliability(assetType, props.asset.id, props.query))
                .then(
                    (
                        res:
                            | AxiosResponse<{
                                  results: { reliability: number; timestamp: string }[];
                              }>
                            | undefined,
                    ) => {
                        if (!res) return;
                        const reliabilityData = [];
                        for (let r = 0; r < res.data.results.length; r++) {
                            const { reliability, timestamp } = res.data.results[r];
                            reliabilityData.push({
                                x: timestamp,
                                y: (reliability || 0) * 100,
                                noMeasurement: isNil(reliability),
                            });
                        }
                        series.value = [
                            {
                                name: 'Reliability',
                                data: reliabilityData,
                                color: '#047857',
                            },
                        ];
                    },
                )
                .catch((e: any) => {
                    if (e?.request?.status === 413)
                        error.value = {
                            message: `Too many data points found with given time period and granularity`,
                            tooltip: `Change selected time period and granularity to reduce number of data points`,
                            icon: LightBulbIcon,
                            classes: 'text-warn-700',
                        };
                    else
                        error.value = {
                            message: `Unable to fetch Reliability visualisation at this
                time...`,
                            tooltip: e?.response?.data?.message,
                            icon: XCircleIcon,
                            classes: `text-danger-700`,
                        };
                });
        };

        const formatDecimals = (value: number) => {
            return parseFloat(Number(value).toFixed(2));
        };

        watch(
            () => props.query,
            () => refetch(),
            { immediate: true, deep: true },
        );

        return { series, options, error, loading, formatDecimals };
    },
});
